import { useState, useEffect, useRef, useCallback } from "react";
import {
  Modal,
  Stack,
  Text,
  Button,
  Group,
  TextInput,
  Textarea,
  Checkbox,
  Box,
  Image,
  Alert,
  Select,
  Progress,
} from "@mantine/core";
import {
  IconCamera,
  IconAlertCircle,
  IconUpload,
  IconRefresh,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { caseImagesService } from "../../services";

interface CameraCaptureModalProps {
  opened: boolean;
  onClose: () => void;
  caseId: string;
  onCaptureSuccess: () => void;
}

interface CameraDevice {
  deviceId: string;
  label: string;
}

export default function CameraCaptureModal({
  opened,
  onClose,
  caseId,
  onCaptureSuccess,
}: CameraCaptureModalProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [cameras, setCameras] = useState<CameraDevice[]>([]);
  const [selectedCameraId, setSelectedCameraId] = useState<string | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [capturedBlob, setCapturedBlob] = useState<Blob | null>(null);
  const [capturedPreview, setCapturedPreview] = useState<string | null>(null);
  const [caption, setCaption] = useState("");
  const [magnification, setMagnification] = useState("");
  const [includeInReport, setIncludeInReport] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isStreaming, setIsStreaming] = useState(false);

  // Enumerate available cameras
  const loadCameras = useCallback(async () => {
    try {
      // Request permission first (needed to get labels)
      await navigator.mediaDevices.getUserMedia({ video: true });
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices
        .filter((d) => d.kind === "videoinput")
        .map((d) => ({
          deviceId: d.deviceId,
          label: d.label || `Camera ${d.deviceId.slice(0, 8)}`,
        }));
      setCameras(videoDevices);
      if (videoDevices.length > 0 && !selectedCameraId) {
        setSelectedCameraId(videoDevices[0].deviceId);
      }
      setCameraError(null);
    } catch (err) {
      setCameraError(
        "Unable to access camera. Please check browser permissions."
      );
    }
  }, [selectedCameraId]);

  // Start video stream from selected camera
  const startStream = useCallback(async () => {
    if (!selectedCameraId) return;

    // Stop existing stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: selectedCameraId } },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setIsStreaming(true);
      setCameraError(null);
    } catch (err) {
      setCameraError(
        "Failed to start camera. It may be in use by another app."
      );
      setIsStreaming(false);
    }
  }, [selectedCameraId]);

  // Stop video stream
  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsStreaming(false);
  }, []);

  // Load cameras when modal opens
  useEffect(() => {
    if (opened) {
      loadCameras();
    } else {
      stopStream();
      setCapturedBlob(null);
      setCapturedPreview(null);
      setCaption("");
      setMagnification("");
      setIncludeInReport(true);
      setCameraError(null);
      setUploadProgress(0);
    }
  }, [opened, loadCameras, stopStream]);

  // Start stream when camera is selected
  useEffect(() => {
    if (opened && selectedCameraId && !capturedBlob) {
      startStream();
    }
  }, [opened, selectedCameraId, capturedBlob, startStream]);

  // Capture a frame from the video
  const handleCapture = () => {
    if (!videoRef.current) return;

    const video = videoRef.current;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.drawImage(video, 0, 0);

    canvas.toBlob(
      (blob) => {
        if (blob) {
          setCapturedBlob(blob);
          setCapturedPreview(URL.createObjectURL(blob));
          stopStream();
        }
      },
      "image/jpeg",
      0.95
    );
  };

  // Retake - go back to live preview
  const handleRetake = () => {
    if (capturedPreview) {
      URL.revokeObjectURL(capturedPreview);
    }
    setCapturedBlob(null);
    setCapturedPreview(null);
    startStream();
  };

  const handleClose = () => {
    if (!uploading) {
      stopStream();
      if (capturedPreview) {
        URL.revokeObjectURL(capturedPreview);
      }
      setCapturedBlob(null);
      setCapturedPreview(null);
      setCaption("");
      setMagnification("");
      setIncludeInReport(true);
      setCameraError(null);
      setUploadProgress(0);
      onClose();
    }
  };

  // Upload captured image
  const handleUpload = async () => {
    if (!capturedBlob) return;

    setUploading(true);
    setUploadProgress(10);

    try {
      // Use a descriptive original filename for camera captures
      const originalFileName = `capture-${Date.now()}.jpg`;

      setUploadProgress(20);
      const { url, key } = await caseImagesService.getPresignedUrl(
        originalFileName,
        "image/jpeg"
      );

      setUploadProgress(40);
      await caseImagesService.uploadBlobToS3(url, capturedBlob, "image/jpeg");

      // Send original filename -- backend generates display name
      setUploadProgress(70);
      await caseImagesService.createImage(caseId, {
        imageUrl: key,
        fileName: originalFileName,
        fileSize: capturedBlob.size,
        mimeType: "image/jpeg",
        caption: caption.trim() || undefined,
        magnification: magnification.trim() || undefined,
        includeInReport,
      });

      setUploadProgress(100);

      notifications.show({
        title: "Success",
        message: "Image captured and uploaded successfully",
        color: "green",
      });

      handleClose();
      onCaptureSuccess();
    } catch (err) {
      notifications.show({
        title: "Upload failed",
        message: "Failed to upload captured image. Please try again.",
        color: "red",
      });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const cameraOptions = cameras.map((c) => ({
    value: c.deviceId,
    label: c.label,
  }));

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group gap="xs">
          <IconCamera size={20} />
          <Text fw={600}>Capture Image</Text>
        </Group>
      }
      centered
      size="lg"
      closeOnClickOutside={false}
    >
      <Stack gap="md">
        {cameraError && (
          <Alert
            icon={<IconAlertCircle size={18} />}
            color="red"
            variant="light"
            withCloseButton
            onClose={() => setCameraError(null)}
          >
            {cameraError}
          </Alert>
        )}

        {/* Camera Selection */}
        {cameras.length > 0 && (
          <Select
            label="Camera"
            data={cameraOptions}
            value={selectedCameraId}
            onChange={(val) => {
              setSelectedCameraId(val);
              if (capturedBlob) {
                handleRetake();
              }
            }}
            disabled={uploading}
          />
        )}

        {/* Live Preview / Captured Image */}
        <Box
          style={{
            border: "1px solid var(--mantine-color-gray-3)",
            borderRadius: "8px",
            overflow: "hidden",
            backgroundColor: "var(--mantine-color-dark-9)",
            minHeight: 300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {capturedPreview ? (
            <Image src={capturedPreview} alt="Captured" fit="contain" h={300} />
          ) : (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                width: "100%",
                maxHeight: 300,
                objectFit: "contain",
                display: isStreaming ? "block" : "none",
              }}
            />
          )}
          {!isStreaming && !capturedPreview && (
            <Text c="dimmed" size="sm">
              {cameras.length === 0
                ? "No cameras detected"
                : "Starting camera..."}
            </Text>
          )}
        </Box>

        {/* Capture / Retake Buttons */}
        {!capturedBlob ? (
          <Button
            fullWidth
            leftSection={<IconCamera size={16} />}
            onClick={handleCapture}
            disabled={!isStreaming || uploading}
            size="md"
          >
            Capture
          </Button>
        ) : (
          <Group grow>
            <Button
              variant="light"
              leftSection={<IconRefresh size={16} />}
              onClick={handleRetake}
              disabled={uploading}
            >
              Retake
            </Button>
          </Group>
        )}

        {/* Metadata fields (shown after capture) */}
        {capturedBlob && (
          <>
            <Textarea
              label="Caption"
              placeholder="e.g., Adenocarcinoma infiltrating muscularis propria (H&E, 40x)"
              value={caption}
              onChange={(e) => setCaption(e.currentTarget.value)}
              disabled={uploading}
              minRows={2}
            />

            <TextInput
              label="Magnification (optional)"
              placeholder="e.g., 40x, 100x, 200x"
              value={magnification}
              onChange={(e) => setMagnification(e.currentTarget.value)}
              disabled={uploading}
            />

            <Checkbox
              label="Include in final report"
              checked={includeInReport}
              onChange={(e) => setIncludeInReport(e.currentTarget.checked)}
              disabled={uploading}
            />

            {uploading && (
              <Box>
                <Text size="xs" c="dimmed" mb={4}>
                  Uploading... {uploadProgress}%
                </Text>
                <Progress value={uploadProgress} animated />
              </Box>
            )}

            <Group justify="flex-end" gap="sm">
              <Button
                variant="light"
                onClick={handleClose}
                disabled={uploading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpload}
                loading={uploading}
                leftSection={<IconUpload size={16} />}
              >
                Upload to Case
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  );
}
